
import React, { PropsWithChildren, useEffect, useState } from 'react';
import { MappedComponentProperties } from '@adobe/aem-react-editable-components';

import './InterestEarningCalculator.css';
import { Button, Icon, Section, Title as TitleCL, ToolTip } from '../../importCL';
import { InputDropdown, InputText } from '../../component-library';
import { useWidthContext } from '../../providers/Width';
import Chart from './Chart/Chart';
import { termConfig, usdFormatter } from './configs/constants';
import { useTermRatesContext } from '../../providers/TermRates/TermRates';
import { PathUtils } from '@adobe/aem-spa-page-model-manager';
import { IDigitalBankTerms, IRate } from '../../types/shared/DigitalBankTerms';
import { retrieveDBOsgiUrl } from '../../utils/cob-utils';

type InterestEarningCalculatorProps = {
    heading?: string;
    subheading?: string;
    tooltipMessage?: string;
    usingDigitalBankTermsProvider?: boolean;
    icon?: {
        iconLabel: string
        iconPath: string;
        uuid: string;
        iconRedirect: string;
    };
    imageAltText?: string;
    ctaHeading?: string;
    ctaSubheading?: string;
    ctaVariant?: 'solid' | 'ghosted-dark';
    ctaSize?: 'small' | 'medium' | 'large';
    ctaId?: string;
} & MappedComponentProperties;

type FormData = {
    deposit: {
        value: string,
        state: 'neutral' | 'error'
    },
    term: {
        selectedTerm: string,
        apy: number,
        rate: number,
        redirectLink: string,
    }
}

type ProductInformation = {
    active: boolean;
    apy: number;
    link: string;
    product: string;
    publishDate: string;
    rate: number;
    selected: boolean;
    term: string;
}

export const InterestEarningCalculatorEditConfig = {
    emptyLabel: 'Interest Earning Calculator',
    isEmpty: (props: PropsWithChildren<InterestEarningCalculatorProps>) => {
        return !props || !props.heading;
    }
};

const InterestEarningCalculator = (props: PropsWithChildren<InterestEarningCalculatorProps>): JSX.Element => {

    const [formData, setFormData] = useState<FormData>({
        deposit: { value: '100000', state: 'neutral' },
        term: {
            selectedTerm: '0',
            apy: 0,
            rate: 0,
            redirectLink: '',
        },
    });
    const [labels, setLabels] = useState<string[]>([]);
    const [interest, setInterest] = useState<number[]>([]);
    const [totalInterest, setTotalInterest] = useState<string>('');
    const [terms, setTerms] = useState<ProductInformation[]>();
    const { width } = useWidthContext();
    const { termRates } = useTermRatesContext();

    useEffect(() => {
        let termsMetadata: IDigitalBankTerms;

        if (!props.usingDigitalBankTermsProvider && Object.keys(termRates).length !== 0) {
            termsMetadata = termRates;
        } else if (props.usingDigitalBankTermsProvider) {
            termsMetadata = JSON.parse(PathUtils.getMetaPropertyValue('digitalBankTerms') as string);
        } else {
            return;
        }

        const termsKey = getDigitalBankTermType();

        if (!termsKey) {
            return;
        }

        const terms = (termsMetadata[termsKey] as IRate[])
            .sort((a, b) => parseInt(a?.term ?? '') - parseInt(b?.term ?? ''))
            .map((rate, i) => ({
                term: rate?.term ?? '',
                apy: Number(rate?.apy ?? 0),
                rate: Number(rate?.apr ?? 0),
                link: rate?.url ?? '',
                product: 'standard',
                active: true,
                selected: i === 0,
                publishDate: '',
            }));

        const initialProduct = terms[0];
        const initialTerm = {
            selectedTerm: initialProduct.term,
            apy: initialProduct.apy,
            rate: initialProduct.rate / 100,
            redirectLink: retrieveDBOsgiUrl(initialProduct.link),
        };
        setTerms(terms);
        setFormData(prev => ({
            ...prev,
            term: initialTerm
        }));
    }, [termRates]);

    useEffect(() => {
        handleData();
    }, [terms]);

    function getDigitalBankTermType(): keyof IDigitalBankTerms | null {
        const pathname = window.location.href;
        if (pathname.includes('deposits/cd')) {
            return 'jumboCd';
        } else if (pathname.includes('deposits/bump-cd')) {
            return 'bumpUpCd';
        } else if (pathname.includes('deposits/savings')) {
            return 'highYieldSavings';
        }
        return null;
    };

    function createDropdownOptions() {
        if (terms && terms.length) {
            const dropdownOptions = [];
            terms.forEach((item) => {
                const termOption = {
                    option: `${(item.term)} Months`,
                    value: item.term
                }
                dropdownOptions.push(termOption);
            });
            return dropdownOptions;
        }
        return [];
    };

    function handleData() {
        const monthlyInterestPercentage = formData.term.rate / 12;
        let runningTotal = 0;
        let growingDeposit = parseFloat(getCleanDeposit());
        const shallowInterest = [];
        const config = termConfig[formData.term.selectedTerm];

        if (config) {
            for (let i = 0; i < parseInt(formData.term.selectedTerm); i++) {
                const interestGain = growingDeposit * monthlyInterestPercentage;
                runningTotal += interestGain
                growingDeposit += interestGain;
                const hasBreakpoint = config.months.some((month) => {
                    return width <= 769 ? month.value === String(i + 1) && !month.removable : month.value === String(i + 1)
                });
                if (hasBreakpoint) {
                    shallowInterest.push(runningTotal);
                }
            }
            setTotalInterest(usdFormatter.format(runningTotal));
            setInterest(shallowInterest);
            handleLabels(formData.term.selectedTerm);
        }
    }

    function handleLabels(termLength: string) {
        const config = termConfig[termLength];
        const shallowLabels = [];
        for (const month of config.months) {
            if (width <= 769) {
                if (!month.removable) {
                    shallowLabels.push(month.value)
                }
            } else {
                shallowLabels.push(month.value);
            }
        }
        setLabels(shallowLabels);
    }

    function getCleanDeposit() {
        return formData.deposit.value.replace(',', '')
            .replace('$', '')
            .replace(/^0/g, '')
            .replace(/[^0-9. ]+/g, '');
    }

    function getFormattedDeposit() {
        if (formData.deposit.value.length) {
            const input = getCleanDeposit()
            if (input !== '') {
                return '$' + parseFloat(input).toLocaleString('en-US');
            }
        }
        return '';
    }

    function validateDeposit() {
        const depositAmount = parseFloat(getCleanDeposit());
        if (!depositAmount || depositAmount < 100000 || depositAmount > 1000000) {
            setFormData(prev => ({
                ...prev,
                deposit: { ...prev.deposit, state: 'error' }
            }));
        }
    }

    const setFormCallback = (value: string, field: 'deposit' | 'term') => {
        if (field === 'deposit') {
            setFormData(prev => ({
                ...prev,
                [field]: { value: value, state: 'neutral' },
            }));
        } else {
            const currentProduct = terms.find((product) => product.term === value);
            const currentTerm = {
                selectedTerm: currentProduct.term,
                apy: currentProduct.apy,
                rate: currentProduct.rate / 100,
                redirectLink: retrieveDBOsgiUrl(currentProduct.link),
            }
            setFormData(prev => ({
                ...prev,
                [field]: currentTerm,
            }));
        }
    };

    return (
        <Section className='InterestEarningCalculator' title={props.heading} bgColor='light-grey' spanTitle={props.subheading} horizontalTitle>
            <div className='InterestEarningCalculator__form gap-6 flex items-start md:gap-0 md:flex-col'>
                <InputText
                    label='Initial Deposit'
                    name='Initial Deposit'
                    additionalText={formData.deposit.state !== 'error' ? 'Please enter an amount between $100,000 - $1,000,000' : undefined}
                    value={getFormattedDeposit()}
                    setValue={(value) => setFormCallback(value, 'deposit')}
                    onBlur={validateDeposit}
                    maxLength={10}
                    state={formData.deposit.state}
                    errorMessage='Please enter an amount between $100,000 - $1,000,000'
                    useSuccessIcon={false}
                />
                <InputDropdown
                    className='w-2/5 md:w-full'
                    name='Term Length'
                    label='Choose A Term Length'
                    setValue={(value) => setFormCallback(value, 'term')}
                    value={formData.term.selectedTerm}
                    options={createDropdownOptions()}
                    alternate
                />
                <Button className='mt-6 !mb-0 md:mt-0 md:!mb-4 flex-1 self-start' onClick={() => handleData()} variant='solid' disabled={formData.deposit.state === 'error'}>Calculate</Button>
            </div>
            <div className='flex flex-col rounded-2xl bg-white'>
                <div className='flex items-center text-white bg-blue-creditone p-4 rounded-t-2xl'>
                    <div className='InterestEarningCalculator__apy flex justify-center items-center pr-16 md:pr-4 md:flex-col md:items-start'>
                        <span>APY: </span>
                        <span className='ml-4 md:ml-0 font-bold text-3xl md:text-base'>{formData.term.apy.toFixed(2)}%</span>
                    </div>
                    <div className='flex items-center ml-16 md:ml-4 md:flex-col md:items-start'>
                        <div className='flex items-center'>
                            <span>Interest Earning Potential: </span>
                            <ToolTip className='ml-2 text-black' fill='white'>{props.tooltipMessage}</ToolTip>
                        </div>
                        <span className='ml-4 md:ml-0 font-bold text-3xl md:text-base '>{totalInterest}</span>
                    </div>
                </div>
                <Chart labels={labels} data={interest} width={width} />
                <div className='flex bg-blue-onahau p-4 rounded-b-2xl justify-center items-center md:flex-col gap-6 md:items-start'>
                    {width > 769 && <div className='InterestEarningCalculator__icon'>
                        <Icon size='badge' src={props.icon?.iconPath} alt={props.imageAltText} background />
                    </div>}
                    <div>
                        <TitleCL tag='h4'>{props.ctaHeading}</TitleCL>
                        <p className='!mb-0'>{props.ctaSubheading}</p>
                    </div>
                    <Button className='ml-auto !mb-0 md:ml-0' variant={props.ctaVariant} id={props.ctaId} size={props.ctaSize} href={formData.term.redirectLink}>Open An Account</Button>
                </div>
            </div>
        </Section>
    );
};

export default InterestEarningCalculator;
